import { useEffect, useState } from "react";
import "./index.scss";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Actions as ApiCallActions } from "../../../redux/apiCall/reducers";
import Input from "../../../components/input";
import { ValidationMessages, ValidationPatterns } from "../../../utils/validation";
import general from "../../../utils/general";
import useI18n from "../../../i18n/useI18n";
import LanguageSwitcher from "../../../components/language-switcher";

const assetStylesheets = [
  "/assets/plugins/bootstrap/css/bootstrap.min.css",
  "/assets/css/main.css",
  "/assets/css/authentication.css",
  "/assets/css/color_skins.css",
];

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useI18n();
  const [navOpen, setNavOpen] = useState(false);
  const [formData, setFormData] = useState({ email: "", code: "", newPassword: "" });
  const [validationErrors, setValidationErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // Make sure public asset styles are available when this route mounts
  useEffect(() => {
    assetStylesheets.forEach((href) => {
      const existing = document.querySelector(`link[href="${href}"]`);
      if (!existing) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        document.head.appendChild(link);
      }
    });
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    if (navOpen) {
      html.classList.add("nav-open");
    } else {
      html.classList.remove("nav-open");
    }
    return () => html.classList.remove("nav-open");
  }, [navOpen]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleValidationChange = (fieldName, isValid, errorMessage) => {
    const updatedErrors = {
      ...validationErrors,
      [fieldName]: isValid ? "" : errorMessage,
    };
    setValidationErrors(updatedErrors);
    setIsFormValid(Object.values(updatedErrors).every((error) => error === ""));
  };

  // Cooldown timer for resend
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const canSendCode = !isSending && cooldown === 0;

  const handleSendCode = (event) => {
    event.preventDefault();
    const isEmailOk = formData.email && !validationErrors.email;
    if (!isEmailOk) {
      general.notificationError("Lütfen geçerli bir e-posta adresi giriniz.");
      return;
    }
    if (!canSendCode) {
      return;
    }

    setIsSending(true);
    dispatch(
      ApiCallActions.Post({
        url: "auth/forgot-password",
        data: { email: formData.email },
        query: { lang: i18n.language },
        showAlertOnError: true,
        showLoading: true,
        onSuccess: ({ data }) => {
          const message =
            data?.message ||
            "Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.";
          general.messageSuccess(message);
          setCodeSent(true);
          setCooldown(60); // 60 saniye beklet
        },
        onError: ({ errorMessage }) => {
          if (errorMessage) {
            general.notificationError(errorMessage);
          }
        },
        callback: () => setIsSending(false),
      })
    );
  };

  const handleVerifyCode = (event) => {
    event.preventDefault();
    const isEmailOk = formData.email && !validationErrors.email;
    const isCodeOk = formData.code && !validationErrors.code;
    const isPasswordOk = formData.newPassword && !validationErrors.newPassword;

    if (!isEmailOk) {
      general.notificationError("Lütfen geçerli bir e-posta adresi giriniz.");
      return;
    }

    if (!isCodeOk) {
      general.notificationError("Lütfen e-posta ile gelen kodu giriniz.");
      return;
    }

    if (!isPasswordOk) {
      general.notificationError("Lütfen yeni şifrenizi giriniz.");
      return;
    }

    setIsVerifying(true);
    dispatch(
      ApiCallActions.Post({
        url: "auth/reset-password",
        data: { email: formData.email, code: formData.code, newPassword: formData.newPassword },
        showAlertOnError: true,
        showLoading: true,
        onSuccess: ({ data }) => {
          const message =
            data?.message || "Şifreniz başarıyla güncellendi. Giriş yapabilirsiniz.";
          general.messageSuccess(message);
          navigate("/auth/login");
        },
        onError: ({ errorMessage }) => {
          if (errorMessage) {
            general.notificationError(errorMessage);
          }
        },
        callback: () => setIsVerifying(false),
      })
    );
  };

  return (
    <section className="theme-orange authentication sidebar-collapse forgot-password">
      <nav className="navbar navbar-expand-lg fixed-top navbar-transparent">
        <div className="container" style={{ justifyContent: 'flex-end', marginRight: 15 }}>

          <div>
            <ul className="navbar-nav navbar-flex-row">
              <li className="nav-item">
                <a className="nav-link" href="https://www.yosdanismanim.com/" target="_blank" rel="noreferrer">
                  {t("COMMON_HOME")}
                </a>
              </li>
              <li className="nav-item">
                <a
                  className="nav-link"
                  title={t("AUTH_NAV_TWITTER_TITLE")}
                  href="https://x.com/yosdanismanim"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="zmdi zmdi-twitter"></i>
                  {/* <p className="d-lg-none d-xl-none">{t("COMMON_SOCIAL_TWITTER")}</p> */}
                </a>
              </li>
              <li className="nav-item">
                <a
                  className="nav-link"
                  title={t("AUTH_NAV_YOUTUBE_TITLE")}
                  href="https://www.youtube.com/@yosdanismanim"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="zmdi zmdi-youtube"></i>
                  {/* <p className="d-lg-none d-xl-none">{t("COMMON_SOCIAL_YOUTUBE")}</p> */}
                </a>
              </li>
              <li className="nav-item">
                <a
                  className="nav-link"
                  title={t("AUTH_NAV_INSTAGRAM_TITLE")}
                  href="https://www.instagram.com/yosdanismanim/?igsh=MTZ4cTB3ejBwcDVqbg%3D%3D#"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="zmdi zmdi-instagram"></i>
                  {/* <p className="d-lg-none d-xl-none">{t("COMMON_SOCIAL_INSTAGRAM")}</p> */}
                </a>
              </li>
              <li className="nav-item" >
                <div className="navbar-lang-switcher" style={{ marginTop: 13, marginLeft: 5 }}>
                  <LanguageSwitcher />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <div className="page-header">
        <div
          className="page-header-image"
          style={{ backgroundImage: "url(/assets/images/login.jpg)" }}
        ></div>
        <div className="container">
          <div className="col-md-12 content-center">
            <div className="card-plain">
              <form className="form" onSubmit={codeSent ? handleVerifyCode : handleSendCode}>
                <div className="header">
                  <div className="logo-container">
                    <img
                      src="https://www.yosdanismanim.com/wp-content/uploads/2025/11/yos-logo.png"
                      alt="YÖS Portal"
                    />
                  </div>
                  <h5>Şifremi Unuttum</h5>
                  <p className="description">
                    E-posta adresinizi girin, şifre sıfırlama bağlantısını gönderelim.
                  </p>
                </div>

                <div className="content">
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="E-posta"
                    icon="zmdi zmdi-email"
                    required
                    validationRegex={ValidationPatterns.EMAIL}
                    errorMessage={ValidationMessages.EMAIL}
                    onValidationChange={handleValidationChange}
                    disabled={codeSent}
                  />

                  {codeSent && (
                    <div className="code-section">
                      <Input
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        placeholder="E-posta ile gelen kod"
                        icon="zmdi zmdi-key"
                        required
                        validationRegex={ValidationPatterns.NOT_EMPTY}
                        errorMessage={ValidationMessages.REQUIRED}
                        onValidationChange={handleValidationChange}
                      />
                      <Input
                        type="password"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleChange}
                        placeholder="Yeni şifre"
                        icon="zmdi zmdi-lock"
                        required
                        showPasswordToggle
                        validationRegex={ValidationPatterns.PASSWORD_MIN}
                        errorMessage={ValidationMessages.PASSWORD_MIN}
                        onValidationChange={handleValidationChange}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-round btn-block btn-lg"
                        onClick={handleVerifyCode}
                        disabled={isVerifying}
                      >
                        {isVerifying ? "Doğrulanıyor..." : "Kodu Doğrula ve Şifreyi Sıfırla"}
                      </button>
                    </div>
                  )}
                </div>

                <div className="footer text-center">
                  <button
                    type={codeSent ? "button" : "submit"}
                    className="btn btn-warning btn-round btn-lg btn-block"
                    disabled={!canSendCode}
                    onClick={codeSent ? (e) => handleSendCode(e) : undefined}
                  >
                    {cooldown > 0
                      ? `Yeniden göndermek için ${cooldown} sn`
                      : codeSent
                        ? "Kodu Tekrar Gönder"
                        : "Sıfırlama Bağlantısını Gönder"}
                  </button>
                  <div className="auth-links fontweight500" >
                    <Link to="/auth/login" className="link">
                      Giriş Yap
                    </Link>
                    <span className="separator">•</span>
                    <Link to="/auth/register" className="link">
                      Kayıt Ol
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <footer className="footer">
          <div className="container">
            <nav>
              <ul>
                <li>
                  <a href="https://www.yosdanismanim.com/iletisim/" target="_blank" rel="noreferrer">
                    İletişim
                  </a>
                </li>
                <li>
                  <a href="https://www.yosdanismanim.com/tarihce/" target="_blank" rel="noreferrer">
                    Hakkımızda
                  </a>
                </li>
                <li>
                  <a href="#sss">SSS</a>
                </li>
              </ul>
            </nav>
            <div className="copyright">
              &copy; {new Date().getFullYear()},{" "}
              <span>
                <a className="text-warning" href="http://algoraft.com/" target="_blank" rel="noreferrer">
                  Algoraft.com
                </a>
                &nbsp;Tarafından Tasarlandı ve Geliştirildi{" "}
              </span>
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
};

export default ForgotPassword;

